import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
} from "../../../components/ui";
import { formatCreatedOn, formatPrice } from "../../brand/planFormat";
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import type { ClientPayment, ClientPaymentStatus } from "../types/clients.types";

/** The height the cards stand in at while the history is on its way — about
 *  three cards, which is what the tab shows before the fold. */
const CARDS_SKELETON = "h-[300px]";

const formatDate = (iso: string): string => formatCreatedOn(new Date(iso));

interface ClientPaymentCardsProps {
  payments: ClientPayment[];
  /** Stands in for the cards when the history comes back with none. */
  empty: EmptyStateProps;
  /** Where the history is — the same contract the table works to. */
  phase?: LoadPhase;
  /** The two answers an outstanding card offers, off with their reason while
   *  the workspace has no plan behind it. */
  gate: GateProps;
  /** The tint each settled state is drawn in, handed over so the two layouts
   *  cannot end up colouring the same status differently. */
  statusStyles: Record<ClientPaymentStatus, string>;
  /** Asks the "money is in" question, and the other one beside it. */
  onAnswer: (payment: ClientPayment, kind: "paid" | "cancel") => void;
}

/**
 * A client's payment history as it reads on a phone: one card per request
 * instead of a table, and no card around the lot of them.
 *
 * The table needs 900px for its eight columns. Seven of them mean something —
 * the eighth is the row's own number, which is a way of finding your place in a
 * table and has nothing to find on a card — and all seven fit on a card's two
 * lines. So unlike the revenue list there is no sheet behind this: nothing is
 * being held back, and a press that revealed one more field would be a press
 * for nothing.
 *
 * The card is not a link. A payment has no screen of its own, and what a row is
 * *for* is the two answers at the foot of an outstanding one — the same tick
 * and cross the table puts in its Status cell, in the same colours, asking the
 * same two questions. A settled request has nothing left to ask and shows the
 * pill instead.
 *
 * Which of the two renders is `ClientPaymentsTab`'s decision — see
 * `useIsMobile`.
 */
const ClientPaymentCards: React.FC<ClientPaymentCardsProps> = ({
  payments,
  empty,
  phase = "ready",
  gate,
  statusStyles,
  onAnswer,
}) => {
  if (phase !== "ready" && payments.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (payments.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
      <ul className="flex flex-col gap-3 pb-4">
        {payments.map((payment) => (
          <li key={payment.id}>
            <div className="card-surface border border-line bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                {/* `min-w-0` is what lets a long plan name shorten instead of
                    pushing the answer beside it off the card's edge. */}
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-medium text-ink">
                    {payment.planName}
                  </h3>
                  <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                    {payment.goal}
                  </p>
                </div>

                {/* An outstanding request is not a state to report so much as a
                    question to answer, so its corner is the two answers rather
                    than a pill saying what the missing "Paid" date below
                    already makes plain. */}
                {payment.status === "Not paid" ? (
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {/* Said for a screen reader, which cannot see that the
                        corner holds a tick and a cross rather than a word. */}
                    <span className="sr-only">Not paid</span>
                    <PaymentAnswer
                      tone="confirm"
                      label={`Mark ${formatPrice(payment.amount)} for ${payment.planName} as received`}
                      gate={gate}
                      onClick={() => onAnswer(payment, "paid")}
                    />
                    <PaymentAnswer
                      tone="cancel"
                      label={`Decline the ${formatPrice(payment.amount)} requested for ${payment.planName}`}
                      gate={gate}
                      onClick={() => onAnswer(payment, "cancel")}
                    />
                  </div>
                ) : (
                  <span
                    className={`flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      statusStyles[payment.status]
                    }`}
                  >
                    {payment.status}
                  </span>
                )}
              </div>

              <div className="mt-3 border-t border-line pt-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[16px] font-semibold text-ink">
                    {formatPrice(payment.amount)}
                  </span>
                  <span className="flex-shrink-0 text-[12px] text-ink-subtle">
                    {payment.duration}
                  </span>
                </div>
                {/* Both dates on one line, each named — a bare pair of dates
                    says nothing about which is which, and the table had a
                    column heading over each of them to do that job. An
                    outstanding request has no second date, and the line simply
                    stops rather than reading as one lost. */}
                <p className="mt-1 text-[12px] text-ink-subtle">
                  Raised {formatDate(payment.generatedAt)}
                  {payment.paidAt && ` · Paid ${formatDate(payment.paidAt)}`}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ClientPaymentCards;
